import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  controlTypes,
  controlableTypes,
  mergeModbusRowsToDataPoints,
  getModbusRowsByKey,
  objectKeyToRegisterKey,
} from '../../screens/Control/ControlComponents/ModbusSchedule/constants';

/**
 * Manages the schedule's action list: selected registers, their current
 * values, and add/update/remove interactions, plus the numeric-picker and
 * "add control" selection-menu UI state that goes with it. Kept separate
 * from the schedule's own name/days/time fields.
 */
const useScheduleControlPoints = ({ modbusUnit, isPowerMeterUnit, setDirty }) => {
  const [scheduleActions, setScheduleActions] = useState({});
  const [activePickerId, setActivePickerId] = useState(null);
  const [showSelectionMenu, setShowSelectionMenu] = useState(false);
  const activePickerRef = useRef(null);

  // Close the open numeric picker on any click outside it.
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (activePickerRef.current && activePickerRef.current.contains(e.target)) {
        return;
      }
      setActivePickerId(null);
    };

    if (activePickerId !== null) {
      window.addEventListener('click', handleClickOutside);
    }

    return () => {
      window.removeEventListener('click', handleClickOutside);
    };
  }, [activePickerId]);

  // Use the same merged data as the live control panel, so non-editable points
  // are excluded here too instead of relying on the row's uiType alone.
  const registerMap = useMemo(() => {
    if (isPowerMeterUnit) {
      return {};
    }
    return mergeModbusRowsToDataPoints(modbusUnit?.rows, modbusUnit?.uiTemplate?.points);
  }, [modbusUnit, isPowerMeterUnit]);

  // All rows, including read-only ones, only used to resolve display names for
  // saved schedule points that are no longer controllable, never for new points.
  const allRowsByKey = useMemo(() => {
    return getModbusRowsByKey(modbusUnit?.rows);
  }, [modbusUnit]);

  // Schedule API uses the numeric register-type key (e.g. "2:40012"),
  // unlike the app's "hreg:40012" lookup key. Each entry already stores
  // its API objectKey, so use it directly.
  const scheduleActionsPayload = useMemo(
    () =>
      Object.entries(scheduleActions).map(([registerKey, action]) => ({
        object: action.objectKey ?? registerKey,
        value: action.value,
      })),
    [scheduleActions]
  );

  /** Rebuilds scheduleActions from a saved schedule's modbusObjects list. */
  const initializeFromModbusObjects = useCallback(
    (modbusObjects = []) => {
      // Keep saved objects that no longer resolve to a controllable point, but mark
      // them disabled so the user can see and remove outdated schedule actions.
      const initialActions = modbusObjects.reduce((acc, item) => {
        const { object: objectKey, value } = item || {};
        if (!objectKey) {
          return acc;
        }

        const registerKey = objectKeyToRegisterKey(objectKey);
        const mapKey = registerKey || objectKey;
        const registerEntry = registerKey ? registerMap?.[registerKey] : null;
        if (registerEntry) {
          acc[mapKey] = {
            id: mapKey,
            ...registerEntry,
            value,
          };
          return acc;
        }

        const staleRow = registerKey ? allRowsByKey?.[registerKey] : null;
        acc[mapKey] = {
          id: mapKey,
          objectKey,
          label: staleRow?.label ?? objectKey,
          config: staleRow?.config ?? {},
          value,
          disabled: true,
        };
        return acc;
      }, {});

      setScheduleActions(initialActions);
    },
    [registerMap, allRowsByKey]
  );

  const handleObjectValueChange = useCallback(
    (objectId, value) => {
      setScheduleActions((prev) => {
        // Prevent unnecessary updates if value hasn't changed
        if (prev[objectId]?.value === value) {
          return prev;
        }

        return {
          ...prev,
          [objectId]: {
            ...prev[objectId],
            value,
          },
        };
      });

      setDirty(true);
    },
    [setDirty]
  );

  const handleDeleteCustomRow = useCallback(
    (objectId) => {
      setScheduleActions((prev) => {
        // eslint-disable-next-line no-unused-vars
        const { [objectId]: removed, ...remaining } = prev;
        return remaining;
      });
      setDirty(true);
    },
    [setDirty]
  );

  // Options for the "Add Controls" selector: writable points not already on the schedule.
  const writableRegisterOptions = useMemo(
    () =>
      Object.entries(registerMap)
        .filter(
          ([key, entry]) =>
            !Object.prototype.hasOwnProperty.call(scheduleActions, key) &&
            controlableTypes[entry?.controlType]
        )
        .map(([key, entry]) => ({
          label: entry?.label ?? entry?.config?.name ?? key,
          value: key,
        })),
    [registerMap, scheduleActions]
  );

  const handleSelectCustomRow = useCallback(
    (registerKey) => {
      if (!registerKey) {
        return;
      }

      const registerEntry = registerMap[registerKey] || {};
      const isToggleType =
        registerEntry.controlType === controlTypes.toggle ||
        registerEntry.controlType === controlTypes.onOff;

      setScheduleActions((prev) => ({
        ...prev,
        [registerKey]: {
          id: registerKey,
          ...registerEntry,
          value: isToggleType ? false : 0, // Set default value false for toggle/onOff types
        },
      }));
      setDirty(true);
      setShowSelectionMenu(false);
    },
    [registerMap, setDirty]
  );

  const handleNumericClick = useCallback((pointId) => {
    setActivePickerId((prev) => (prev === pointId ? null : pointId));
  }, []);

  const openSelectionMenu = useCallback(() => setShowSelectionMenu(true), []);
  const closeSelectionMenu = useCallback(() => setShowSelectionMenu(false), []);

  return {
    registerMap,
    allRowsByKey,
    scheduleActions,
    setScheduleActions,
    scheduleActionsPayload,
    initializeFromModbusObjects,
    handleObjectValueChange,
    handleDeleteCustomRow,
    writableRegisterOptions,
    handleSelectCustomRow,
    handleNumericClick,
    activePickerId,
    activePickerRef,
    showSelectionMenu,
    openSelectionMenu,
    closeSelectionMenu,
  };
};

export default useScheduleControlPoints;
